import { useState, useEffect, useMemo } from 'react';
import { loadTimeseries } from '../../../data/timeseriesClient';
import { INDICATOR_SERIES } from '../../../data/seriesConfig';
import {
    BTC_PORTFOLIO_MANAGER_SIGNAL_SERIES,
    ETH_PORTFOLIO_MANAGER_SIGNAL_SERIES,
} from './portfolioManagerSignal';
import { indicatorValue } from './indicatorSeriesOptions';
import type { TimeRange } from '../components/charts/PriceChart';

export interface IndicatorSegment {
    start: Date;
    end: Date;
    value: number; // 0-1
}

export interface IndicatorOverlay {
    segments: IndicatorSegment[];
}

// Mirrors RANGE_DAYS in useAssetClustering
const RANGE_DAYS: Record<TimeRange, number> = {
    '1W': 7,
    '1M': 30,
    '3M': 90,
    '1Y': 365,
    ALL: 365 * 15,
};

// BTC_SERIES is still used by the altcoin overlay below (altcoin = altcoin_season
// × the BTC rotation series). The BTC/ETH asset-page overlays no longer use these
// rotation series — they use the live bot signal series defined further down.
const BTC_SERIES = INDICATOR_SERIES.find(s => s.key === 'btc')!;
const ALTCOIN_SERIES = INDICATOR_SERIES.find(s => s.key === 'altcoin')!;

type IndicatorKey = 'btc' | 'eth' | 'altcoin';

function tickerToKey(ticker: string): IndicatorKey {
    const t = ticker.toUpperCase();
    if (t === 'BTC') return 'btc';
    if (t === 'ETH') return 'eth';
    return 'altcoin';
}

// Extract numeric value from a raw timeseries point using seriesConfig getValue
function extractValue(
    series: (typeof INDICATOR_SERIES)[number],
    point: any
): number {
    return series.getValue(point?.data ?? {}) ?? 0;
}

// Align two timeseries by timestamp (ISO string), inner-joining on matching days.
// Returns array of { t, primary, btc } where primary is altcoin raw value.
function alignAndMultiply(
    altcoinPoints: any[],
    btcPoints: any[]
): Array<{ timestamp: string; value: number }> {
    const btcMap = new Map<string, number>();
    for (const p of btcPoints) {
        if (typeof p?.timestamp === 'string') {
            btcMap.set(p.timestamp, extractValue(BTC_SERIES, p));
        }
    }

    const result: Array<{ timestamp: string; value: number }> = [];
    for (const p of altcoinPoints) {
        if (typeof p?.timestamp !== 'string') continue;
        const altVal = extractValue(ALTCOIN_SERIES, p);
        const btcVal = btcMap.get(p.timestamp);
        if (btcVal === undefined) continue;
        result.push({ timestamp: p.timestamp, value: altVal * btcVal });
    }
    return result;
}

function buildSegments(
    points: Array<{ timestamp: string; value: number }>
): IndicatorSegment[] {
    const raw: IndicatorSegment[] = points
        .map(p => {
            const t = new Date(p.timestamp);
            return { start: t, end: t, value: p.value };
        })
        .filter(s => !isNaN(s.start.getTime()));

    return raw
        .map((seg, i) => ({
            ...seg,
            start: i === 0 ? new Date(0) : seg.start,
            end: raw[i + 1]?.start ?? new Date(),
        }))
        .filter(seg => seg.start.getTime() !== seg.end.getTime());
}

/**
 * Which series drives the BTC/ETH band, and how to read its value.
 * Optional: callers that do not offer a choice keep the previous behaviour
 * (the live portfolio manager's in/out signal).
 */
export interface IndicatorSeriesOverride {
    series: string;
    /** Continuous dial (weight in the asset) rather than an in/out switch. */
    fractional: boolean;
}

export function useIndicatorOverlay(
    ticker: string | null | undefined,
    timeRange: TimeRange,
    override?: IndicatorSeriesOverride
): { overlay: IndicatorOverlay | null; loading: boolean; available: boolean } {
    const [loading, setLoading] = useState(false);
    const [segments, setSegments] = useState<IndicatorSegment[]>([]);

    const key = ticker ? tickerToKey(ticker) : null;

    useEffect(() => {
        if (!key) {
            setSegments([]);
            return;
        }

        const controller = new AbortController();
        setLoading(true);

        const end = new Date();
        const start = new Date(
            end.getTime() - RANGE_DAYS[timeRange] * 24 * 60 * 60 * 1000
        );
        const opts = { from: start, to: end, signal: controller.signal };

        const fetch =
            key === 'altcoin'
                ? Promise.all([
                      loadTimeseries(ALTCOIN_SERIES.series, opts),
                      loadTimeseries(BTC_SERIES.series, opts),
                  ]).then(([altcoin, btc]) =>
                      buildSegments(alignAndMultiply(altcoin, btc))
                  )
                : loadTimeseries(
                      override?.series ??
                          (key === 'btc'
                              ? BTC_PORTFOLIO_MANAGER_SIGNAL_SERIES
                              : ETH_PORTFOLIO_MANAGER_SIGNAL_SERIES),
                      opts
                  ).then(points =>
                      buildSegments(
                          points
                              .filter(
                                  (p: any) => typeof p?.timestamp === 'string'
                              )
                              .map((p: any) => ({
                                  timestamp: p.timestamp,
                                  value: indicatorValue(
                                      p?.data,
                                      override?.fractional ?? false,
                                      key === 'eth' ? 'eth' : 'btc'
                                  ),
                              }))
                      )
                  );

        fetch
            .then(segs => setSegments(segs))
            .catch(err => {
                if (err.name !== 'AbortError')
                    console.error('useIndicatorOverlay: fetch failed', err);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [key, timeRange, override?.series, override?.fractional]);

    const overlay = useMemo(
        () => (segments.length > 0 ? { segments } : null),
        [segments]
    );

    return { overlay, loading, available: key !== null };
}
